/**
 * Cross-tab coordination of the session refresh (2026-10 re-test: payroll
 * #6, books #7, leave/approvals #6 — "reload after the access token expires
 * lands on /login although a refresh would succeed", and the multi-tab race).
 *
 * The refresh token ROTATES, and presenting an already-rotated one revokes
 * every session of the user (backend Refresh → 401 refresh_reuse). Inside one
 * tab client.ts coalesces concurrent 401s onto ONE refresh. Tabs, though,
 * share the cookie jar but not that in-memory gate: two tabs whose access
 * cookie expired together each POSTed /auth/refresh with the SAME refresh
 * cookie, and the loser tripped the reuse detector.
 *
 * Two best-effort mechanisms close that:
 *  1. An exclusive Web Lock (navigator.locks, "wr-auth-refresh") serialises
 *     the refresh POSTs of every tab of this origin, so no two are ever in
 *     flight with the same token — the second one waits, and by then the
 *     browser holds the rotated cookie.
 *  2. A "refreshed at" timestamp in localStorage lets a tab that waited for
 *     the lock see that a peer already rotated the cookies AFTER its own
 *     failed request went out: it skips its rotation and just replays.
 * Without Web Locks (an old browser, node tests) the in-tab coalescing still
 * applies.
 *
 * The pieces that touch browser globals sit behind RefreshCoordination so
 * the unit tests can drive two "tabs" through the real interceptor
 * (auth-boot-refresh.test.ts). Plain .ts, no imports.
 */

export interface RefreshCoordination {
  /** Run `fn` while holding the origin-wide refresh lock. */
  withLock<T>(fn: () => Promise<T>): Promise<T>;
  /** When a tab of this browser last completed a refresh (epoch ms). */
  lastRefreshedAt(): number | null;
  /** Record a completed refresh. */
  markRefreshed(at: number): void;
  /** The clock both of the above use. */
  now(): number;
}

const LOCK_NAME = "wr-auth-refresh";
const MARKER_KEY = "wr_auth_refreshed_at";

/**
 * How long a peer tab's refresh is trusted to have rotated the cookies a
 * waiting request needs. A refresh holds the lock for at most
 * REFRESH_TIMEOUT_MS, so a waiter always sees a marker younger than this; an
 * older (or future-dated, after a clock change) marker is ignored rather
 * than skip a refresh that is really needed.
 */
export const PEER_REFRESH_TRUST_MS = 60_000;

/** The refresh POST's timeout — it bounds how long the lock is held. */
export const REFRESH_TIMEOUT_MS = 30_000;

type LockManagerLike = {
  request(name: string, options: { mode: "exclusive" }, cb: () => Promise<unknown>): Promise<unknown>;
};

function browserLocks(): LockManagerLike | null {
  if (typeof navigator === "undefined") return null;
  const locks = (navigator as unknown as { locks?: LockManagerLike }).locks;
  return locks && typeof locks.request === "function" ? locks : null;
}

function browserStorage(): Storage | null {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null; // storage disabled / sandboxed iframe
  }
}

export function browserRefreshCoordination(): RefreshCoordination {
  return {
    withLock<T>(fn: () => Promise<T>): Promise<T> {
      const locks = browserLocks();
      if (!locks) return fn();
      return locks.request(LOCK_NAME, { mode: "exclusive" }, fn) as Promise<T>;
    },
    lastRefreshedAt(): number | null {
      try {
        const raw = browserStorage()?.getItem(MARKER_KEY);
        const at = raw ? Number(raw) : NaN;
        return Number.isFinite(at) ? at : null;
      } catch {
        return null;
      }
    },
    markRefreshed(at: number): void {
      try {
        browserStorage()?.setItem(MARKER_KEY, String(at));
      } catch {
        /* quota / disabled — the lock alone still serialises */
      }
    },
    now: () => Date.now(),
  };
}

let coordination: RefreshCoordination = browserRefreshCoordination();

export function refreshCoordination(): RefreshCoordination {
  return coordination;
}

/** Tests install a fake (null restores the browser one). */
export function setRefreshCoordination(c: RefreshCoordination | null): void {
  coordination = c ?? browserRefreshCoordination();
}

/**
 * True when a peer tab completed a refresh after `staleSince` — the moment
 * the failed request was sent — so the browser already holds the rotated
 * cookies and this tab must not rotate again (it replays instead). A missing,
 * future-dated or stale marker is never trusted.
 */
export function peerRefreshedSince(staleSince: number, lastRefreshed: number | null, now: number): boolean {
  if (lastRefreshed == null) return false;
  if (lastRefreshed > now || now - lastRefreshed > PEER_REFRESH_TRUST_MS) return false;
  return lastRefreshed > staleSince;
}
